import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, LogIn, KeyRound, Wallet, Users } from "lucide-react";
import axios, { API, fmt, fmtTime, pctColor } from "./commoditiesLib";
import { EventCalendarCard } from "./EventCalendarCard";

// 2026-10-05 — Commodities Live Orders console (Phase 1) + linked family
// Kite accounts (Phase 2). Chat: "Live Orders : Commodities...enable this for
// multiple users? where I can manage (Buy/Sell/Cancel/Get Open Positions)?"
// Everything here talks to ONE selected Kite account (primary or a linked
// family account); the backend (kite_console.py) re-checks access on every
// call — an admin sees every account, anyone else only the ones they own.

const btn = "px-2 py-1 border text-[10px] uppercase tracking-wider font-mono-data hover:bg-white/5 disabled:opacity-40";
const th = "px-3 py-2";
const errMsg = (e, d) => e?.response?.data?.detail || d;

const Badge = ({ ok, children }) => (
  <span className="text-[10px] font-mono-data uppercase px-1.5 py-0.5 border"
        style={{ color: ok ? "var(--bull)" : "var(--bear)", borderColor: ok ? "var(--bull)" : "var(--bear)" }}>
    {children}
  </span>
);

// 2026-10-10 — today's autopilot mirror orders for a linked account (kite_mirror.py)
const MirrorPanel = ({ acc }) => {
  const [rows, setRows] = useState(null);
  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/kite-accounts/${acc.account_id}/mirror`);
      setRows(data.rows || []);
    } catch (e) { setRows([]); }
  }, [acc.account_id]);
  useEffect(() => { load(); const id = setInterval(load, 15000); return () => clearInterval(id); }, [load]);
  if (rows == null) return null;
  const colr = (st) => (["complete", "placed", "open"].includes(st) ? "var(--bull)" : ["rejected", "skipped"].includes(st) ? "var(--bear)" : undefined);
  return (
    <div className="border border-grid overflow-x-auto" data-testid="kite-mirror-panel">
      <div className="px-3 py-2 border-b border-grid text-xs uppercase tracking-[0.2em] font-mono-data text-muted-2">
        Autopilot mirror · today · {acc.label} {acc.live_enabled ? "(LIVE)" : "(DRY RUN)"}
      </div>
      {!rows.length ? (
        <div className="px-3 py-3 text-xs text-muted-3 font-mono-data">No mirrored orders today.</div>
      ) : (
        <table className="w-full text-[11px] font-mono-data">
          <thead className="text-muted-3 text-[9px] uppercase tracking-[0.15em]">
            <tr className="border-b border-grid">
              {["Time", "Strategy", "Side", "Contract", "Qty", "Type", "Price", "Filled", "Avg", "Status", "Reason"].map((h) => (
                <th key={h} className="text-left px-3 py-1.5 font-normal">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-grid/40">
                <td className="px-3 py-1.5">{fmtTime(r.created_at)}</td>
                <td className="px-3 py-1.5 text-muted-2">{r.source}</td>
                <td className="px-3 py-1.5">{r.side}</td>
                <td className="px-3 py-1.5">{r.tradingsymbol}</td>
                <td className="px-3 py-1.5">{r.quantity}</td>
                <td className="px-3 py-1.5">{r.order_type}</td>
                <td className="px-3 py-1.5">{r.price || "—"}</td>
                <td className="px-3 py-1.5">{r.mode === "dry_run" ? (r.would_fill_qty ?? 0) : (r.filled_qty ?? 0)}</td>
                <td className="px-3 py-1.5">{r.mode === "dry_run" ? (r.would_avg_price ?? "—") : (r.avg_price ?? "—")}</td>
                <td className="px-3 py-1.5" style={{ color: colr(r.status) }}>{r.status}</td>
                <td className="px-3 py-1.5 text-muted-3">{r.reason || r.cancel_reason || ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

// ───────────── main console ─────────────
const KiteConsole = () => {
  const [accounts, setAccounts] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [sel, setSel] = useState(null);
  const [sum, setSum] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadAccounts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/kite-accounts`);
      setAccounts(data.accounts || []);
      setIsAdmin(!!data.is_admin);
      setSel((cur) => cur && (data.accounts || []).some((a) => a.account_id === cur) ? cur : (data.accounts || [])[0]?.account_id || null);
    } catch (e) {
      /* not signed in / no access — render nothing */
    }
  }, []);

  const loadSummary = useCallback(async () => {
    if (!sel) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/kite-console/${sel}/summary`);
      setSum(data);
    } catch (e) {
      toast.error(errMsg(e, "Couldn't load Kite account"));
    } finally {
      setLoading(false);
    }
  }, [sel]);

  useEffect(() => { loadAccounts(); }, [loadAccounts]);
  useEffect(() => { setSum(null); loadSummary(); const t = setInterval(loadSummary, 20000); return () => clearInterval(t); }, [loadSummary]);
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    if (q.get("kite_account")) {
      const st = q.get("kite");
      st === "connected" ? toast.success(`Kite connected for ${q.get("kite_account")}`) : toast.error(`Kite login for ${q.get("kite_account")}: ${st}`);
      setSel(q.get("kite_account"));
    }
  }, []);

  const acc = useMemo(() => (sum?.account && sum.account.account_id === sel ? sum.account : accounts.find((a) => a.account_id === sel)), [sum, accounts, sel]);
  if (!accounts.length) return null;

  const refreshAll = () => { loadAccounts(); loadSummary(); };
  const act = async (fn, okMsg) => {
    try { const r = await fn(); toast.success(okMsg(r?.data)); refreshAll(); } catch (e) { toast.error(errMsg(e, "Failed")); }
  };
  const exitPos = (p) => {
    const lim = window.prompt(`Exit ${p.tradingsymbol} (${p.quantity}) on ${acc.label}.\nLeave blank for MARKET, or enter a LIMIT price:`, "");
    if (lim === null) return;
    // 2026-10-31 — reason tag (P&L by reason): 1 Target hit · 2 Stop loss · 3 Trailing · 4 Reversal · 5 End of day · 6 Fear · 7 Booked early, or free text
    const EXITS = ["Target hit", "Stop loss", "Trailing / locking profit", "Reversal signal", "Time / end of day", "Fear / uncertainty", "Booked early"];
    const why = window.prompt(`Why exit? (optional)\n${EXITS.map((r, i) => `${i + 1} ${r}`).join(" · ")}\nType a number or your own reason:`, "");
    const reason = why && /^[1-7]$/.test(why.trim()) ? EXITS[Number(why.trim()) - 1] : (why || "").trim() || undefined;
    const body = { tradingsymbol: p.tradingsymbol, exchange: p.exchange, product: p.product, order_type: lim ? "LIMIT" : "MARKET",
                   price: lim ? Number(lim) : 0, reason };
    act(() => axios.post(`${API}/kite-console/${sel}/positions/exit`, body), (d) => `Exit ${d.side} ${d.quantity} placed · order ${d.order_id}`);
  };
  const cancelOrd = (o) => {
    if (!window.confirm(`Cancel order ${o.order_id} (${o.transaction_type} ${o.tradingsymbol})?`)) return;
    act(() => axios.post(`${API}/kite-console/${sel}/orders/${o.order_id}/cancel`), () => `Cancelled ${o.order_id}`);
  };
  const modifyOrd = (o) => {
    const p = window.prompt(`New LIMIT price for ${o.tradingsymbol} (now ₹${o.price}):`, String(o.price || ""));
    if (!p) return;
    // 2026-11-02 — quantity can be changed too (e.g. grow a working SELL after adding lots)
    const q = window.prompt(`Quantity for ${o.transaction_type} ${o.tradingsymbol} (now ${o.quantity}, filled ${o.filled_quantity || 0}):`, String(o.quantity || ""));
    if (q === null) return;
    const body = { price: Number(p), order_type: "LIMIT", ...(q && Number(q) !== Number(o.quantity) ? { quantity: Number(q) } : {}) };
    act(() => axios.post(`${API}/kite-console/${sel}/orders/${o.order_id}/modify`, body),
        () => `Modified ${o.order_id} → ₹${p}${body.quantity ? ` × ${body.quantity}` : ""}`);
  };

  return (
    <div className="space-y-4" data-testid="kite-console">
      {/* 2026-11-02 — next scheduled event per commodity (event_calendar.py, log only) */}
      <EventCalendarCard compact />
      <div className="border border-grid p-4 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h3 className="text-xs uppercase tracking-[0.2em] font-mono-data text-muted-2 flex items-center gap-2">
            <Users size={12} /> Kite accounts · console
          </h3>
          <div className="flex gap-2">
            <button className={btn} onClick={refreshAll} disabled={loading}><RefreshCw size={11} className="inline" /> Refresh</button>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {accounts.map((a) => (
            <button key={a.account_id} onClick={() => setSel(a.account_id)}
                    className="px-3 py-1.5 border text-[11px] font-mono-data flex items-center gap-2"
                    style={{ borderColor: a.account_id === sel ? "var(--bull)" : "var(--grid)" }}
                    data-testid={`kite-account-tab-${a.account_id}`}>
              {a.label}
              <span className="w-1.5 h-1.5 rounded-full"
                    style={{ background: !a.enabled ? "var(--text-muted-3)" : a.connected ? "var(--bull)" : "var(--bear)" }} />
              {!a.enabled && <span className="text-[9px] text-muted-3 uppercase">pending</span>}
            </button>
          ))}
        </div>
        {acc && (
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-[11px] font-mono-data">
            {acc.enabled === false
              ? <Badge ok={false}>awaiting admin approval</Badge>
              : <Badge ok={acc.connected}>{acc.connected ? "connected today" : "login needed"}</Badge>}
            <span className="text-muted-2">Owner <span className="text-muted-1">{acc.owner_name || "—"}</span></span>
            {acc.kite_user_id && <span className="text-muted-2">Zerodha <span className="text-muted-1">{acc.kite_user_id}</span></span>}
            {sum?.funds && <span className="text-muted-2 flex items-center gap-1"><Wallet size={11} /> Commodity margin available <span className="text-muted-1">₹{fmt(sum.funds.commodity_available, 0)}</span></span>}
            {/* 2026-11-03 — the daily cap counts automated (copied) orders only; manual orders are uncapped */}
            {sum?.auto_orders_today != null && <span className="text-muted-2">Auto orders today <span className="text-muted-1">{sum.auto_orders_today}{acc.max_orders_per_day ? ` / ${acc.max_orders_per_day}` : ""}</span></span>}
            {sum?.orders_today_via_app != null && <span className="text-muted-2">Manual orders today <span className="text-muted-1">{sum.orders_today_via_app}</span>{!acc.primary ? <span className="text-muted-3"> (no cap)</span> : null}</span>}
            {acc.enabled !== false && (
              <a className={btn} href={`${API}/kite-accounts/${acc.account_id}/login`}><LogIn size={11} className="inline" /> Login with Kite</a>
            )}
            {acc.enabled !== false && (acc.primary || acc.autologin_configured) && (
              <button className={btn} onClick={() => act(() => axios.post(`${API}/kite-accounts/${acc.account_id}/auto-login`),
                                                          (d) => (d?.ok ? "Auto-login OK" : `Auto-login: ${d?.error}`))}>
                <KeyRound size={11} className="inline" /> Auto-login now
              </button>
            )}
            {acc.last_login_ok === false && <span style={{ color: "var(--bear)" }}>Last auto-login failed: {acc.last_login_error}</span>}
          </div>
        )}
        {acc && !acc.primary && (
          <div className="text-[10px] font-mono-data text-muted-3">
            Credentials come from {acc.owner_name}&apos;s Settings → Zerodha API Credentials
            {acc.autologin_configured ? " (auto-login ready)" : " (add user id, password & TOTP there for daily auto-login)"}.
            Kite app Redirect URL must be <span className="text-muted-2">{acc.redirect_url}</span>.
          </div>
        )}
        {isAdmin && acc && !acc.primary && (
          // 2026-10-15 — copy settings moved to Admin → Users (checkboxes per setup)
          <div className="text-[11px] font-mono-data border-t border-grid/60 pt-3 flex flex-wrap gap-x-4 gap-y-1 text-muted-2"
               data-testid="kite-account-controls-summary">
            <span style={{ color: acc.enabled ? "var(--bull)" : "var(--bear)" }}>{acc.enabled ? "Copy ENABLED" : "Copy DISABLED"}</span>
            <span>{acc.live_enabled ? "Real orders ON" : "Dry run"}</span>
            <span>Max ₹{fmt(acc.max_margin_rs, 0)} / order · {acc.max_orders_per_day} auto orders/day (manual not capped) · lots ×{acc.lots_multiplier} · loss stop ₹{fmt(acc.max_daily_loss_rs, 0)}</span>
            <span>Follows: {(acc.mirror_strategies || []).join(", ") || "nothing"}</span>
            <a href="/admin/users" className="underline" style={{ color: "var(--bull)" }}>Change in Admin → Users</a>
          </div>
        )}
      </div>


      {acc && !acc.primary && acc.enabled && <MirrorPanel acc={acc} />}

      {acc && sum?.connected && (
        <>
          {/* 2026-10-26 — the order ticket moved to the unified Manual Order page */}
          <div className="border border-grid p-3 text-xs font-mono-data flex items-center justify-between gap-2">
            <span className="text-muted-2">Place stock (NFO) or commodity (MCX) option orders for {acc.label}:</span>
            <a href="/manual-order" className={btn} style={{ color: "var(--bull)", borderColor: "var(--bull)" }} data-testid="kite-console-open-terminal">Open Manual Order</a>
          </div>

          <div className="border border-grid overflow-x-auto">
            <div className="px-3 py-2 border-b border-grid text-xs uppercase tracking-[0.2em] font-mono-data text-muted-2">
              Open positions · {acc.label}
            </div>
            {(sum.open_positions || []).length === 0 ? (
              <div className="p-4 text-xs text-muted-2 font-mono-data">No open positions.</div>
            ) : (
              <table className="w-full text-xs">
                <thead><tr className="text-left text-muted-3 uppercase tracking-wider text-[10px] font-mono-data border-b border-grid">
                  <th className={th}>Contract</th><th className={th}>Product</th><th className={`${th} text-right`}>Qty</th>
                  <th className={`${th} text-right`}>Avg</th><th className={`${th} text-right`}>LTP</th><th className={`${th} text-right`}>P&amp;L ₹</th><th className={`${th} text-right`}>Action</th>
                </tr></thead>
                <tbody>
                  {sum.open_positions.map((p) => (
                    <tr key={`${p.tradingsymbol}-${p.product}`} className="border-b border-grid/40">
                      <td className={`${th} font-semibold`}>{p.tradingsymbol}<span className="text-muted-3 font-normal"> {p.exchange}</span></td>
                      <td className={`${th} text-muted-2`}>{p.product}</td>
                      <td className={`${th} text-right`}>{p.quantity}</td>
                      <td className={`${th} text-right text-muted-2`}>{fmt(p.average_price)}</td>
                      <td className={`${th} text-right text-muted-2`}>{fmt(p.last_price)}</td>
                      <td className={`${th} text-right font-semibold`} style={{ color: pctColor(p.pnl) }}>{fmt(p.pnl, 0)}</td>
                      <td className={`${th} text-right`}>
                        {(p.exchange === "MCX" || p.exchange === "NFO") && (
                          <button className={btn} style={{ color: "var(--bear)", borderColor: "var(--bear)" }} onClick={() => exitPos(p)}>Exit</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="border border-grid overflow-x-auto">
            <div className="px-3 py-2 border-b border-grid text-xs uppercase tracking-[0.2em] font-mono-data text-muted-2">
              Today's orders · {acc.label}
            </div>
            {(sum.orders || []).length === 0 ? (
              <div className="p-4 text-xs text-muted-2 font-mono-data">No orders today.</div>
            ) : (
              <table className="w-full text-xs">
                <thead><tr className="text-left text-muted-3 uppercase tracking-wider text-[10px] font-mono-data border-b border-grid">
                  <th className={th}>Time</th><th className={th}>Contract</th><th className={th}>Side</th><th className={th}>Type</th>
                  <th className={`${th} text-right`}>Qty / filled</th><th className={`${th} text-right`}>Price</th><th className={`${th} text-right`}>Avg</th>
                  <th className={th}>Status</th><th className={`${th} text-right`}>Action</th>
                </tr></thead>
                <tbody>
                  {sum.orders.map((o) => (
                    <tr key={o.order_id} className="border-b border-grid/40">
                      <td className={`${th} text-muted-3 font-mono-data text-[10px]`}>{String(o.order_timestamp || "").slice(11, 19)}</td>
                      <td className={th}>{o.tradingsymbol}{o.tag ? <span className="text-muted-3 text-[10px]"> · {o.tag}</span> : null}</td>
                      <td className={th} style={{ color: o.transaction_type === "BUY" ? "var(--bull)" : "var(--bear)" }}>{o.transaction_type}</td>
                      <td className={`${th} text-muted-2`}>{o.order_type} {o.product}</td>
                      <td className={`${th} text-right`}>{o.quantity} / {o.filled_quantity ?? 0}</td>
                      <td className={`${th} text-right text-muted-2`}>{fmt(o.price)}</td>
                      <td className={`${th} text-right text-muted-2`}>{fmt(o.average_price)}</td>
                      <td className={`${th} font-mono-data text-[10px]`} title={o.status_message || ""}>{o.status}</td>
                      <td className={`${th} text-right space-x-1`}>
                        {o.is_open && (
                          <>
                            {o.order_type === "LIMIT" && <button className={btn} onClick={() => modifyOrd(o)}>Modify</button>}
                            <button className={btn} style={{ color: "var(--bear)", borderColor: "var(--bear)" }} onClick={() => cancelOrd(o)}>Cancel</button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {(sum.trades || []).length > 0 && (
            <details className="border border-grid">
              <summary className="px-3 py-2 text-xs uppercase tracking-[0.2em] font-mono-data text-muted-2 cursor-pointer">
                Today's trades ({sum.trades.length})
              </summary>
              <table className="w-full text-xs">
                <tbody>
                  {sum.trades.map((t) => (
                    <tr key={t.trade_id} className="border-t border-grid/40">
                      <td className={`${th} text-muted-3 text-[10px]`}>{fmtTime(t.fill_timestamp) || t.fill_timestamp}</td>
                      <td className={th}>{t.tradingsymbol}</td>
                      <td className={th} style={{ color: t.transaction_type === "BUY" ? "var(--bull)" : "var(--bear)" }}>{t.transaction_type}</td>
                      <td className={`${th} text-right`}>{t.quantity}</td>
                      <td className={`${th} text-right`}>{fmt(t.average_price)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}
        </>
      )}
      {acc && sum?.pending_approval && (
        <div className="border border-grid p-4 text-xs font-mono-data text-muted-2">
          Your Zerodha credentials are saved. The admin needs to enable this account before it can be used here.
        </div>
      )}
      {acc && sum && !sum.connected && !sum.pending_approval && (
        <div className="border border-grid p-4 text-xs font-mono-data text-muted-2">
          {acc.label} isn't logged in to Kite today{sum.error ? ` (${sum.error})` : ""}. Use “Login with Kite”
          {acc.autologin_configured || acc.primary ? " or “Auto-login now”" : ""} above.
        </div>
      )}
    </div>
  );
};

export default KiteConsole;
